import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSchedule } from '../context/ScheduleContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { format, parseISO } from 'date-fns';
import { Heart, Search, MapPin, AlertTriangle } from 'lucide-react';
import type { FavoriteConflictMap, Performance } from '../types';

interface Props {
  onlyFavorites?: boolean;
}

const SEARCH_DEBOUNCE_MS = 500;

export const ScheduleList: React.FC<Props> = ({ onlyFavorites = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const { schedules, stages, favorites, toggleFavorite } = useSchedule();
  const [selectedDay, setSelectedDay] = useState(schedules[0]?.day || '');
  const [searchTerm, setSearchTerm] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filteredDataRef = useRef<Array<{ day: string; stages: Array<{ stage: string; performances: Performance[] }> }>>([]);

  const getStageLocation = (stageName: string) => {
    return stages.find(s => s.stage === stageName)?.location;
  };

  const openMap = (stageName: string) => {
    navigate(`/map?stage=${encodeURIComponent(stageName)}`);
  };

  const isSearchMode = searchTerm.length > 0;
  const showAllDays = isSearchMode || onlyFavorites;

  const filteredData = useMemo(() => {
    const daysToProcess = showAllDays 
      ? schedules 
      : schedules.filter(s => s.day === selectedDay);

    return daysToProcess.map(daySchedule => {
      const filteredStages = daySchedule.schedule.map(stageSchedule => {
        const filteredPerformances = stageSchedule.performances.filter(perf => {
          // Filter by favorites
          if (onlyFavorites && !favorites.includes(perf.dj)) return false;
          
          // Filter by search term
          if (searchTerm && !perf.dj.toLowerCase().includes(searchTerm.toLowerCase())) return false;

          return true;
        });

        return {
          ...stageSchedule,
          performances: filteredPerformances
        };
      }).filter(stage => stage.performances.length > 0);

      return {
        day: daySchedule.day,
        stages: filteredStages
      };
    }).filter(day => day.stages.length > 0);

  }, [schedules, selectedDay, onlyFavorites, favorites, searchTerm, showAllDays]);

  const favoriteConflictMap = useMemo(() => {
    type DisplayedFavorite = {
      id: string;
      dj: string;
      start: Date;
      end: Date;
    };

    const getPerformanceId = (performance: Performance, stageName: string, dayName: string) =>
      `${dayName}__${stageName}__${performance.dj}__${performance.start_time}`;

    const displayedFavorites: DisplayedFavorite[] = [];

    filteredData.forEach(day => {
      day.stages.forEach(stage => {
        stage.performances.forEach(performance => {
          if (!favorites.includes(performance.dj)) return;

          displayedFavorites.push({
            id: getPerformanceId(performance, stage.stage, day.day),
            dj: performance.dj,
            start: parseISO(performance.start_time),
            end: parseISO(performance.end_time),
          });
        });
      });
    });

    const conflictMap: FavoriteConflictMap = {};

    const addConflict = (sourceId: string, conflictingDj: string) => {
      if (!conflictMap[sourceId]) conflictMap[sourceId] = [];
      if (!conflictMap[sourceId].includes(conflictingDj)) {
        conflictMap[sourceId].push(conflictingDj);
      }
    };

    for (let i = 0; i < displayedFavorites.length; i += 1) {
      for (let j = i + 1; j < displayedFavorites.length; j += 1) {
        const a = displayedFavorites[i];
        const b = displayedFavorites[j];

        const overlaps = a.start < b.end && b.start < a.end;
        if (!overlaps) continue;

        addConflict(a.id, b.dj);
        addConflict(b.id, a.dj);
      }
    }

    return conflictMap;
  }, [filteredData, favorites]);

  filteredDataRef.current = filteredData;

  useEffect(() => {
    if (searchTerm.length === 0) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const data = filteredDataRef.current;
      const resultCount = data.reduce(
        (sum, d) => sum + d.stages.reduce((s, st) => s + st.performances.length, 0),
        0
      );
      trackEvent('search_used', {
        queryLength: searchTerm.length,
        resultCount,
        onlyFavorites,
      });
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm, onlyFavorites, trackEvent]);

  return (
    <div className="pb-24 overflow-x-hidden">
      {/* Day Tabs - Only show if NOT in search/favorites mode */}
      {!showAllDays && (
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-gray-800">
          <div className="flex overflow-x-auto p-2 gap-2 hide-scrollbar">
            {schedules.map(day => (
              <button
                key={day.day}
                onClick={() => setSelectedDay(day.day)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                  selectedDay === day.day 
                    ? 'bg-primary text-white' 
                    : 'bg-surface text-gray-400 hover:text-white'
                }`}
              >
                {day.day}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search Bar - Always visible */}
      <div className={`p-2 sticky ${!showAllDays ? 'top-14' : 'top-0'} z-10 bg-background/95 backdrop-blur border-b border-gray-800`}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={
              onlyFavorites
                ? t('schedule.searchFavoritesPlaceholder')
                : t('schedule.searchDjPlaceholder')
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-surface border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-primary placeholder-gray-500"
          />
        </div>
      </div>

      {/* Schedule Grid */}
      <div className="p-4 space-y-8">
        {filteredData.length > 0 ? (
          filteredData.map(day => (
            <div key={day.day} className="space-y-6">
              {/* Day Header if showing all days */}
              {showAllDays && (
                <h2 className="text-2xl font-bold text-primary sticky top-14 bg-background/95 backdrop-blur py-2 z-10 border-b border-gray-800">
                  {day.day}
                </h2>
              )}

              {day.stages.map(stage => (
                <div key={stage.stage} className="space-y-2">
                  <div className={`sticky ${showAllDays ? 'top-28' : 'top-32'} bg-background/80 backdrop-blur py-2 z-0 flex justify-between items-center gap-3`}>
                    <h3 className="text-xl font-black text-secondary uppercase tracking-widest min-w-0 truncate">
                      {stage.stage}
                    </h3>
                    {(() => {
                        const loc = getStageLocation(stage.stage);
                        return loc ? (
                            <button 
                                onClick={() => openMap(stage.stage)}
                                className="p-1 text-gray-400 hover:text-white shrink-0"
                                title={t('schedule.navigateToStage')}
                            >
                                <MapPin className="w-5 h-5" />
                            </button>
                        ) : null;
                    })()}
                  </div>
                  <div className="grid gap-2">
                    {stage.performances.map((perf, idx) => (
                      (() => {
                        const performanceId = `${day.day}__${stage.stage}__${perf.dj}__${perf.start_time}`;
                        const conflicts = favoriteConflictMap[performanceId] ?? [];

                        return (
                      <div 
                        key={`${perf.dj}-${idx}`} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          favorites.includes(perf.dj) 
                            ? 'bg-surface border-alert/30' 
                            : 'bg-surface border-gray-800'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-mono text-gray-400">
                              {format(parseISO(perf.start_time), 'HH:mm')} - {format(parseISO(perf.end_time), 'HH:mm')}
                            </span>
                          </div>
                          <h4 className={`font-bold truncate ${favorites.includes(perf.dj) ? 'text-alert' : 'text-white'}`}>
                            {perf.dj}
                          </h4>
                          {favorites.includes(perf.dj) && conflicts.length > 0 && (
                            <div
                              className="mt-1 flex items-center gap-1 text-xs text-alert"
                              title={t('schedule.conflictsWith', { artists: conflicts.join(', ') })}
                            >
                              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">
                                {t('schedule.conflictsWith', { artists: conflicts.join(', ') })}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <button
                          onClick={() => toggleFavorite(perf.dj)}
                          className="ml-4 p-2 rounded-full hover:bg-white/5 active:scale-95 transition-all"
                        >
                          <Heart 
                            className={`w-5 h-5 ${favorites.includes(perf.dj) ? 'fill-alert text-alert' : 'text-gray-600'}`} 
                          />
                        </button>
                      </div>
                        );
                      })()
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="text-center py-12 text-gray-500">
            {t('common.noPerformancesFound')}
          </div>
        )}
      </div>
    </div>
  );
};
